import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  addPublisher,
  createPublisherSyncJob,
  getPublisherEvents,
  listPublishers,
  removePublisher,
  triggerJob,
  type Publisher,
  type PublisherEvent,
} from '../api/client';
import { panelPath } from '../config/paths';
import PageToolbar from '../components/PageToolbar';
import { PublisherEventListItem } from './publisherEventUI';

const PAGE_SIZE = 20;

/** API may omit zero-value ints; Go zero times arrive as "0001-01-01T…". */
function isPresentDate(iso?: string): boolean {
  if (!iso) return false;
  return !iso.startsWith('0001-01-01');
}

const PublisherTrackingPage: React.FC = () => {
  const { t } = useTranslation();

  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [loadingPublishers, setLoadingPublishers] = useState(true);
  const [publishersError, setPublishersError] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const [artistInput, setArtistInput] = useState('');
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [countryInput, setCountryInput] = useState('us');
  const [adding, setAdding] = useState(false);

  const [events, setEvents] = useState<PublisherEvent[]>([]);
  const [cursorBefore, setCursorBefore] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const storefrontByPublisherId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of publishers) {
      if (p.storefront) map[p.id] = p.storefront;
    }
    return map;
  }, [publishers]);

  const loadPublishers = useCallback(async () => {
    setLoadingPublishers(true);
    setPublishersError(null);
    try {
      const res = await listPublishers();
      setPublishers(res.items ?? []);
    } catch (e) {
      setPublishersError(e instanceof Error ? e.message : t('publishers.loadError'));
    } finally {
      setLoadingPublishers(false);
    }
  }, [t]);

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    setEventsError(null);
    try {
      const res = await getPublisherEvents({ limit: PAGE_SIZE });
      const items = res.items ?? [];
      setEvents(items);
      setCursorBefore(items.length > 0 ? items[items.length - 1]!.createdAt : null);
      setHasMore(items.length === PAGE_SIZE);
    } catch (e) {
      setEventsError(e instanceof Error ? e.message : t('publishers.loadError'));
    } finally {
      setLoadingEvents(false);
    }
  }, [t]);

  useEffect(() => {
    void loadPublishers();
    void loadEvents();
  }, [loadPublishers, loadEvents]);

  const loadMoreEvents = useCallback(async () => {
    if (loadingMore || loadingEvents || !hasMore) return;
    setLoadingMore(true);
    setEventsError(null);
    try {
      const res = await getPublisherEvents({ before: cursorBefore ?? undefined, limit: PAGE_SIZE });
      const newItems = res.items ?? [];
      if (newItems.length === 0) {
        setHasMore(false);
        return;
      }
      setEvents((prev) => [...prev, ...newItems]);
      setCursorBefore(newItems[newItems.length - 1]!.createdAt);
      setHasMore(newItems.length === PAGE_SIZE);
    } catch (e) {
      setEventsError(e instanceof Error ? e.message : t('publishers.loadError'));
    } finally {
      setLoadingMore(false);
    }
  }, [cursorBefore, hasMore, loadingEvents, loadingMore, t]);

  const handleAddPublisher = useCallback(async () => {
    if (!artistInput.trim()) return;
    setAdding(true);
    setPublishersError(null);
    try {
      await addPublisher({
        artistIdOrUrl: artistInput.trim(),
        displayName: displayNameInput.trim() || undefined,
        country: countryInput.trim() || undefined,
      });
      setArtistInput('');
      setDisplayNameInput('');
      await loadPublishers();
    } catch (e) {
      setPublishersError(e instanceof Error ? e.message : t('publishers.addError'));
    } finally {
      setAdding(false);
    }
  }, [artistInput, displayNameInput, countryInput, loadPublishers, t]);

  const handleRemovePublisher = useCallback(
    async (id: string) => {
      setRemovingId(id);
      setPublishersError(null);
      try {
        await removePublisher(id);
        await loadPublishers();
      } catch (e) {
        setPublishersError(e instanceof Error ? e.message : t('publishers.removeError'));
      } finally {
        setRemovingId(null);
      }
    },
    [loadPublishers, t]
  );

  const handleSyncNow = useCallback(
    async (id: string) => {
      setSyncingId(id);
      setPublishersError(null);
      try {
        const { jobId } = await createPublisherSyncJob(id);
        triggerJob(jobId).catch((er) => setPublishersError(er instanceof Error ? er.message : t('publishers.syncError')));
        setTimeout(() => {
          void loadPublishers();
          void loadEvents();
        }, 4000);
      } catch (e) {
        setPublishersError(e instanceof Error ? e.message : t('publishers.syncError'));
      } finally {
        setSyncingId(null);
      }
    },
    [loadPublishers, loadEvents, t]
  );

  return (
    <div className="p-4 space-y-6">
      <PageToolbar standalone>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{t('publishers.title')}</h1>
      </PageToolbar>

      {publishersError && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {publishersError}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('publishers.addTitle')}</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">{t('publishers.addHint')}</p>
        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            className="input flex-1 min-w-[240px]"
            placeholder={t('publishers.artistPlaceholder')}
            value={artistInput}
            onChange={(e) => setArtistInput(e.target.value)}
          />
          <input
            type="text"
            className="input w-48"
            placeholder={t('publishers.displayNamePlaceholder')}
            value={displayNameInput}
            onChange={(e) => setDisplayNameInput(e.target.value)}
          />
          <input
            type="text"
            className="input w-20"
            placeholder="us"
            value={countryInput}
            onChange={(e) => setCountryInput(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary px-4 py-2 text-sm disabled:opacity-50"
            disabled={adding || !artistInput.trim()}
            onClick={() => void handleAddPublisher()}
          >
            {adding ? t('publishers.adding') : t('publishers.add')}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('publishers.watchedTitle')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">{t('publishers.colName')}</th>
                <th className="px-4 py-3">{t('publishers.colStorefront')}</th>
                <th className="px-4 py-3">{t('publishers.colTotalApps')}</th>
                <th className="px-4 py-3">{t('publishers.colLastApp')}</th>
                <th className="px-4 py-3">{t('publishers.colLastSync')}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loadingPublishers && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                    …
                  </td>
                </tr>
              )}
              {!loadingPublishers && publishers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                    {t('publishers.empty')}
                  </td>
                </tr>
              )}
              {publishers.map((p) => (
                <tr key={p.id} className="text-gray-700 dark:text-gray-300">
                  <td className="px-4 py-2">
                    <Link
                      to={panelPath(`/publishers/${p.id}`)}
                      className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {p.displayName || p.artistId}
                    </Link>
                  </td>
                  <td className="px-4 py-2 uppercase text-xs">{p.storefront}</td>
                  <td className="px-4 py-2 tabular-nums">{(p.totalApps ?? 0).toLocaleString()}</td>
                  <td className="px-4 py-2 max-w-[220px] truncate" title={p.lastAppName || undefined}>
                    {p.lastAppName ? (
                      <>
                        {p.lastAppName}
                        {isPresentDate(p.lastAppReleaseDate) && (
                          <span className="block text-xs text-gray-400 dark:text-gray-500">
                            {new Date(p.lastAppReleaseDate!).toLocaleDateString()}
                          </span>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-xs">
                    {isPresentDate(p.lastSyncAt)
                      ? new Date(p.lastSyncAt!).toLocaleString()
                      : t('publishers.neverSynced')}
                    {p.lastSyncStatus === 'errored' && (
                      <span className="ml-2 text-red-500">({t('publishers.lastSyncErrored')})</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      className="btn btn-secondary px-3 py-1.5 text-xs disabled:opacity-50 mr-2"
                      disabled={syncingId === p.id}
                      onClick={() => void handleSyncNow(p.id)}
                    >
                      {syncingId === p.id ? t('publishers.syncing') : t('publishers.syncNow')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary px-3 py-1.5 text-xs disabled:opacity-50 text-red-600 dark:text-red-400"
                      disabled={removingId === p.id}
                      onClick={() => void handleRemovePublisher(p.id)}
                    >
                      {t('publishers.remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('publishers.timelineTitle')}</h2>
        </div>
        {eventsError && (
          <div className="px-4 py-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20">
            {eventsError}
          </div>
        )}
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {loadingEvents && <li className="px-4 py-6 text-center text-gray-500 text-sm">…</li>}
          {!loadingEvents && events.length === 0 && (
            <li className="px-4 py-6 text-center text-gray-500 text-sm">{t('publishers.timelineEmpty')}</li>
          )}
          {events.map((ev) => (
            <PublisherEventListItem
              key={ev.id}
              event={ev}
              storefront={storefrontByPublisherId[ev.publisherId]}
            />
          ))}
        </ul>
        {hasMore && events.length > 0 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center">
            <button
              type="button"
              className="btn btn-secondary px-4 py-2 text-sm disabled:opacity-50"
              disabled={loadingMore}
              onClick={() => void loadMoreEvents()}
            >
              {loadingMore ? t('publishers.loadingMore') : t('publishers.loadMore')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublisherTrackingPage;
